// src/pages/InternshipsPage.jsx
import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useIntersect } from '../hooks/useIntersect';

const MEDIA_URL   = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const MODE_LABELS = { in_person:'In-Person', remote:'Remote', hybrid:'Hybrid' };

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

// ── Canonical InternshipCard — imported by AcademyPage too ─
export function InternshipCard({ internship, idx = 0, basePath = '/academy/internships' }) {
  const [ref, visible] = useIntersect();
  const [hover, setHover] = useState(false);
  const cover  = resolveMedia(internship.media_url);
  const isOpen = internship.status === 'open';

  return (
    <Link ref={ref} to={`${basePath}/${internship.slug}`}
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{
        display:'flex', flexDirection:'column', textDecoration:'none',
        background:'var(--bg-2)', borderRadius:16, overflow:'hidden',
        border:`1px solid ${hover?'rgba(232,164,58,0.35)':'var(--border)'}`,
        opacity:visible?1:0,
        transform:visible?(hover?'translateY(-3px)':'translateY(0)'):'translateY(24px)',
        transition:`opacity 0.5s ${idx*0.06}s, transform 0.5s ${idx*0.06}s, border-color 0.2s, box-shadow 0.2s`,
        boxShadow:hover?'0 12px 32px rgba(0,0,0,0.25)':'none',
      }}>

      {/* Cover — always shown, placeholder if no image */}
      <div style={{ height:160, position:'relative', flexShrink:0, background:'var(--bg-3)', overflow:'hidden' }}>
        {cover ? (
          <img src={cover} alt={internship.title}
            style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.5s', transform:hover?'scale(1.06)':'scale(1)' }}
            onError={e=>{ e.target.style.display='none'; }}/>
        ) : (
          <div style={{ width:'100%', height:'100%', background:'linear-gradient(135deg, var(--bg-3) 0%, rgba(232,164,58,0.06) 100%)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem', opacity:0.15 }}>◉</div>
        )}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(8,10,13,0.82) 0%, transparent 55%)' }}/>

        {/* Badges always on image */}
        <div style={{ position:'absolute', top:12, left:12, display:'flex', gap:6, flexWrap:'wrap' }}>
          <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'4px 10px', borderRadius:20, letterSpacing:'0.04em', background:isOpen?'rgba(62,207,142,0.92)':'rgba(60,60,60,0.88)', color:isOpen?'#fff':'rgba(255,255,255,0.7)' }}>
            {isOpen ? 'Open' : (internship.status||'').replace(/_/g,' ')}
          </span>
          {internship.department && (
            <span style={{ fontSize:'0.6rem', fontWeight:600, padding:'4px 10px', borderRadius:20, background:'rgba(8,10,13,0.72)', color:'rgba(255,255,255,0.75)', backdropFilter:'blur(4px)' }}>
              {internship.department}
            </span>
          )}
        </div>
        {internship.is_paid && (
          <span style={{ position:'absolute', top:12, right:12, fontSize:'0.62rem', fontWeight:700, padding:'4px 10px', borderRadius:20, background:'rgba(232,164,58,0.92)', color:'#080a0d' }}>
            Paid
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding:'18px 20px 20px', display:'flex', flexDirection:'column', gap:10, flex:1 }}>
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:600, lineHeight:1.3, color:hover?'var(--gold)':'var(--t-primary)', transition:'color 0.15s' }}>
          {internship.title}
        </h3>
        {internship.description && (
          <p style={{ fontSize:'0.83rem', color:'var(--t-secondary)', lineHeight:1.65, flex:1 }}>
            {internship.description.slice(0,110)}{internship.description.length>110?'…':''}
          </p>
        )}

        <div style={{ display:'flex', gap:16, flexWrap:'wrap', paddingTop:12, borderTop:'1px solid var(--border)', marginTop:'auto' }}>
          {internship.duration && (
            <div>
              <div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Duration</div>
              <div style={{ fontSize:'0.82rem', fontWeight:500 }}>{internship.duration}</div>
            </div>
          )}
          {internship.mode && (
            <div>
              <div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Mode</div>
              <div style={{ fontSize:'0.82rem', fontWeight:500 }}>{MODE_LABELS[internship.mode]||internship.mode}</div>
            </div>
          )}
          {internship.is_paid && internship.stipend_amount && (
            <div>
              <div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Stipend</div>
              <div style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--gold)' }}>RWF {Number(internship.stipend_amount).toLocaleString()}</div>
            </div>
          )}
          <span style={{ marginLeft:'auto', fontSize:'0.82rem', color:'var(--gold)', fontWeight:600, alignSelf:'flex-end' }}>Apply →</span>
        </div>
      </div>
    </Link>
  );
}

// ── List page ─────────────────────────────────────────────
export function InternshipsListPage() {
  const [internships, setInternships] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('open');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicAPI.internships({ status: filter || undefined });
      setInternships(res.data?.data || []);
    } catch { setInternships([]); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <section style={{ paddingTop:'calc(var(--nav-h) + clamp(60px,8vw,100px))', paddingBottom:'clamp(60px,8vw,100px)', background:'linear-gradient(180deg, var(--bg-1) 0%, var(--bg-0) 100%)' }}>
        <div className="container">
          <div className="section-label label" style={{ marginBottom:20 }}>Internships</div>
          <h1 className="display-lg" style={{ marginBottom:16 }}>Learn by Doing.<br/><span style={{ color:'var(--gold)' }}>Build Real Things.</span></h1>
          <p className="body-lg" style={{ color:'var(--t-secondary)', maxWidth:520 }}>Hands-on experience within the MilleHills ecosystem.</p>
        </div>
      </section>
      <div style={{ background:'var(--bg-0)', borderBottom:'1px solid var(--border)', position:'sticky', top:'var(--nav-h)', zIndex:50 }}>
        <div className="container" style={{ padding:'14px clamp(20px,5vw,60px)', display:'flex', gap:8 }}>
          {[['open','Open'],['','All'],['coming_soon','Coming Soon']].map(([val,label])=>(
            <button key={val} onClick={()=>setFilter(val)} style={{ padding:'7px 14px', borderRadius:20, fontSize:'0.78rem', cursor:'pointer', fontWeight:filter===val?700:400, background:filter===val?'var(--gold)':'var(--bg-2)', color:filter===val?'#080a0d':'var(--t-secondary)', border:`1px solid ${filter===val?'var(--gold)':'var(--border)'}`, transition:'all 0.15s' }}>{label}</button>
          ))}
        </div>
      </div>
      <section style={{ padding:'clamp(40px,6vw,80px) 0', minHeight:'50vh' }}>
        <div className="container">
          {loading ? (
            <div style={{ textAlign:'center', padding:80, color:'var(--t-muted)' }}>Loading…</div>
          ) : internships.length === 0 ? (
            <div style={{ textAlign:'center', padding:80 }}>
              <div style={{ fontSize:'2rem', opacity:0.3, marginBottom:12 }}>◈</div>
              <div style={{ color:'var(--t-muted)', marginBottom:16 }}>No internships right now</div>
              <Link to="/contact" className="btn btn-outline" style={{ margin:'0 auto' }}>Express Interest →</Link>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:20 }}>
              {internships.map((i,idx)=><InternshipCard key={i.id} internship={i} idx={idx}/>)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ── Detail page ───────────────────────────────────────────
export function InternshipDetailPage() {
  const { slug }  = useParams();
  const navigate  = useNavigate();
  const [internship, setInternship] = useState(null);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    publicAPI.internshipBySlug(slug)
      .then(r=>setInternship(r.data?.data))
      .catch(()=>setInternship(null))
      .finally(()=>setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ paddingTop:'calc(var(--nav-h) + 80px)', textAlign:'center', color:'var(--t-muted)' }}>Loading…</div>;
  if (!internship) return (
    <div style={{ paddingTop:'calc(var(--nav-h) + 80px)', textAlign:'center' }}>
      Not found. <Link to="/academy?tab=internships" style={{ color:'var(--gold)' }}>← Back</Link>
    </div>
  );

  const cover        = resolveMedia(internship.media_url);
  const isOpen       = internship.status === 'open';
  const deadline     = internship.application_deadline ? new Date(internship.application_deadline) : null;
  const deadlinePassed = deadline && deadline < new Date();

  return (
    <div>
      {cover ? (
        <div style={{ position:'relative', height:'clamp(260px,35vw,420px)', overflow:'hidden' }}>
          <img src={cover} alt={internship.title} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(8,10,13,0.92) 0%, rgba(8,10,13,0.3) 60%, transparent 100%)' }}/>
          <div className="container" style={{ position:'absolute', bottom:0, left:'50%', transform:'translateX(-50%)', width:'100%', padding:'0 0 40px' }}>
            <Link to="/academy?tab=internships" style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.8rem', marginBottom:16, display:'inline-block' }}>← Back to Internships</Link>
            <div style={{ display:'flex', gap:8, marginBottom:14 }}>
              {internship.department && <span className="badge badge-dim">{internship.department}</span>}
              <span style={{ fontSize:'0.6rem', fontWeight:700, padding:'3px 8px', borderRadius:20, background:isOpen?'rgba(62,207,142,0.85)':'rgba(80,80,80,0.85)', color:'#fff' }}>{isOpen?'Open':internship.status}</span>
            </div>
            <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.6rem,4vw,2.4rem)', fontWeight:700, lineHeight:1.1, color:'#fff' }}>{internship.title}</h1>
          </div>
        </div>
      ) : (
        <section style={{ paddingTop:'calc(var(--nav-h) + 60px)', paddingBottom:48, background:'var(--bg-1)', borderBottom:'1px solid var(--border)' }}>
          <div className="container">
            <Link to="/academy?tab=internships" style={{ color:'var(--t-muted)', fontSize:'0.8rem', marginBottom:24, display:'inline-block' }}>← Back</Link>
            <h1 className="display-lg">{internship.title}</h1>
          </div>
        </section>
      )}

      <section style={{ padding:'clamp(40px,6vw,80px) 0' }}>
        <div className="container" style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:60, alignItems:'start' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:32 }}>
            {internship.description && (
              <div>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:600, marginBottom:16 }}>About This Internship</h2>
                <p className="body-lg" style={{ color:'var(--t-secondary)', lineHeight:1.8 }}>{internship.description}</p>
              </div>
            )}
            {internship.skills_required?.length > 0 && (
              <div>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:600, marginBottom:16 }}>Skills Required</h2>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {internship.skills_required.map(s=>(
                    <span key={s} style={{ padding:'6px 14px', borderRadius:20, background:'var(--bg-2)', border:'1px solid var(--border)', fontSize:'0.82rem', color:'var(--t-secondary)' }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ position:'sticky', top:'calc(var(--nav-h) + 24px)', display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', padding:24 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:20 }}>
                {[
                  ['Duration', internship.duration],
                  ['Mode',     MODE_LABELS[internship.mode]||internship.mode],
                  ['Capacity', internship.capacity?`${internship.capacity} position${internship.capacity>1?'s':''}`:null],
                  ['Stipend',  internship.is_paid?`RWF ${Number(internship.stipend_amount||0).toLocaleString()}`:'Volunteer'],
                  deadline?['Deadline', deadline.toLocaleDateString('en-RW',{year:'numeric',month:'long',day:'numeric'})]:null,
                ].filter(Boolean).filter(([,v])=>v).map(([k,v])=>(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:12, paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
                    <span style={{ fontSize:'0.82rem', color:'var(--t-muted)' }}>{k}</span>
                    <span style={{ fontSize:'0.82rem', fontWeight:500, textAlign:'right', color:k==='Deadline'&&deadlinePassed?'var(--red)':'var(--t-primary)' }}>{v}</span>
                  </div>
                ))}
              </div>
              {isOpen && !deadlinePassed ? (
                <button onClick={()=>navigate(`/academy/internships/${slug}/apply`)}
                  className="btn btn-primary" style={{ width:'100%', justifyContent:'center', fontWeight:700 }}>
                  Apply Now →
                </button>
              ) : (
                <div style={{ padding:12, background:'var(--bg-2)', borderRadius:8, textAlign:'center', fontSize:'0.85rem', color:'var(--t-muted)' }}>
                  {deadlinePassed?'Applications closed':'Not accepting applications'}
                </div>
              )}
              <Link to="/contact?type=volunteer" style={{ display:'block', textAlign:'center', marginTop:10, fontSize:'0.8rem', color:'var(--t-muted)' }}>Have questions? →</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}